/**
 * Search + lane filter toolbar for the CRM dashboard.
 *
 * Purely a controlled-input surface: `searchInput`/`lane` and their change
 * handlers are owned by the caller (backed by `useCrmFilters`'s URL state),
 * so this component holds no query state of its own.
 */

import type { ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import type { DashboardLane } from '@/lib/crm/dashboard';

export const LANE_LABELS: Record<DashboardLane, string> = {
  'my-turn': 'My Turn',
  'their-turn': 'Their Turn',
  scheduled: 'Scheduled',
  nurture: 'Nurture',
  'on-hold': 'On Hold',
};

const LANE_OPTIONS = (Object.keys(LANE_LABELS) as DashboardLane[]).map((lane) => ({
  value: lane,
  label: LANE_LABELS[lane],
}));

interface CrmToolbarProps {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  lane: DashboardLane | null;
  onLaneChange: (lane: DashboardLane | null) => void;
  onClearFilters: () => void;
  /**
   * Whether any of the three filters this toolbar's Clear affordance resets
   * (search `q`, `lane`, or the stat-card `due`) is currently active. The
   * Clear button is the single clear-everything control for the whole
   * dashboard (see `index.tsx`), so it only makes sense to show it once
   * there's something to clear.
   */
  hasActiveFilters: boolean;
  resultSummary: string;
}

export function CrmToolbar({
  searchInput,
  onSearchInputChange,
  lane,
  onLaneChange,
  onClearFilters,
  hasActiveFilters,
  resultSummary,
}: CrmToolbarProps): JSX.Element {
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onSearchInputChange(event.target.value);
  };

  const handleLaneChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const { value } = event.target;
    onLaneChange(value ? (value as DashboardLane) : null);
  };

  return (
    <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
      <div className="md:max-w-sm md:flex-1">
        <Input
          type="search"
          className="h-11 md:h-11"
          aria-label="Search contacts by name or company"
          placeholder="Search contacts..."
          value={searchInput}
          onChange={handleSearchChange}
        />
      </div>

      <div className="md:w-56">
        <NativeSelect aria-label="Filter by lane" value={lane ?? ''} onChange={handleLaneChange}>
          <option value="">All lanes</option>
          {LANE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelect>
      </div>

      {hasActiveFilters && (
        <Button
          type="button"
          variant="ghost"
          className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
          onClick={onClearFilters}
        >
          Clear filters
        </Button>
      )}

      <p className="text-sm text-muted-foreground md:ml-auto" role="status">
        {resultSummary}
      </p>
    </div>
  );
}
